import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { adminMe, setAdminToken, getAdminToken, invoiceAiListClients } from "@/lib/api";
import { useSessionRenewal } from "@/hooks/useSessionRenewal";

export default function InvoiceClients() {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getAdminToken()) { nav("/admin/login", { replace: true }); return; }
    adminMe()
      .then((res) => { if (res?.expires_at) setAdminToken(getAdminToken(), res.expires_at); })
      .catch(() => { setAdminToken(""); nav("/admin/login", { replace: true }); });
  }, [nav]);

  useSessionRenewal({
    onExpired: () => { setAdminToken(""); nav("/admin/login", { replace: true }); },
  });

  useEffect(() => {
    invoiceAiListClients()
      .then((res) => setClients(res.clients || []))
      .catch(() => toast.error("Could not load invoice clients."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-10" data-testid="admin-invoice-clients-page">
      <SEO title="Invoice Clients" noIndex />
      <div className="flex items-center gap-3 mb-2">
        <Users className="text-[#3A7A8C]" size={22} />
        <h1 className="text-2xl font-bold text-[#080808]">Invoice Clients</h1>
      </div>
      <p className="text-sm text-[#6B7280] mb-8">
        Saved billing recipients the{" "}
        <Link to="/admin/invoices" className="underline hover:text-[#3A7A8C]">
          Invoice AI Assistant
        </Link>{" "}
        can reuse. Managed conversationally — ask the assistant to save or update a client.
      </p>

      {loading ? (
        <Loader2 className="animate-spin text-[#3A7A8C]" size={22} />
      ) : clients.length === 0 ? (
        <p className="text-sm text-[#9CA3AF]" data-testid="invoice-clients-empty">
          No saved clients yet.
        </p>
      ) : (
        <div className="overflow-x-auto border border-[#E5E7EB] rounded-lg">
          <table className="w-full text-sm" data-testid="invoice-clients-table">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Name</th>
                <th className="text-left font-semibold px-4 py-3">Billing Contact</th>
                <th className="text-left font-semibold px-4 py-3">Address</th>
                <th className="text-left font-semibold px-4 py-3">Email</th>
                <th className="text-left font-semibold px-4 py-3">Phone</th>
                <th className="text-left font-semibold px-4 py-3">Notes</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-b border-[#F3F4F6]" data-testid={`invoice-client-row-${c.id}`}>
                  <td className="px-4 py-3 font-semibold">{c.name}</td>
                  <td className="px-4 py-3">{c.billing_contact || "—"}</td>
                  <td className="px-4 py-3">{c.billing_address || "—"}</td>
                  <td className="px-4 py-3">{c.email || "—"}</td>
                  <td className="px-4 py-3">{c.phone || "—"}</td>
                  <td className="px-4 py-3 text-[#6B7280]">{c.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
